import { z } from 'zod'
import { formatDate } from '~/prix/types/date'
import { formatAsBrNumber } from '~/prix/utils/types/number'
import type { RuralReportTemplateProps } from '../../report/ruralReportTemplateTitle'
import { semenType } from '../../semenStock/semenStockObject'

export const inseminationReportTypeSchema = z.enum([
  'allInseminations',
  'statistics',
  'deliveriesByPeriod',
  'deliveriesByInseminator',
  'deliveriesBySemenType',
  'deliveriesByBull',
])

export type InseminationReportType = z.infer<typeof inseminationReportTypeSchema>
export type InseminationStatisticsGrouping = 'inseminator' | 'residence' | 'semenType'
export type InseminationDeliveryGrouping = 'period' | 'inseminator' | 'semenType' | 'bull'

export interface InseminationReportEntry {
  id: string
  inseminationDate?: Date | string | null
  ownerId: string
  ownerName: string
  earringNumber: number | null
  inseminatorId: string | null
  inseminatorName: string
  residenceName: string
  semenTypeName: string
  bullId: string | null
  bullName: string
  bullBreed: string
  programName: string
  isRepetition: boolean
}

export interface InseminationStatisticsRow {
  grouping: InseminationStatisticsGrouping
  label: string
  repetitionInseminationCount: number
  nonRepetitionInseminationCount: number
  totalInseminationCount: number
  successAverage: number
}

export interface InseminationStatisticsData {
  totalRepetitionInseminations: number
  totalNonRepetitionInseminations: number
  totalInseminations: number
  successAverage: number
  rows: InseminationStatisticsRow[]
  inseminationEntries: InseminationReportEntry[]
}

export interface InseminationDeliveryEntry {
  id: string
  inseminationDate?: Date | string | null
  ownerName: string
  inseminatorId: string | null
  inseminatorName: string
  bullId: string | null
  bullName: string
  bullBreed: string
  semenTypeName: string
  deliveredQuantity: number
  availableQuantity: number
}

export interface InseminationDeliverySummaryRow {
  grouping: Exclude<InseminationDeliveryGrouping, 'period'>
  label: string
  deliveredQuantity: number
  availableQuantity: number
}

export interface InseminationDeliveryData {
  grouping: InseminationDeliveryGrouping
  startDate?: Date | string | null
  endDate?: Date | string | null
  totalDeliveredQuantity: number
  totalAvailableQuantity: number
  rows: InseminationDeliveryEntry[] | InseminationDeliverySummaryRow[]
}

export interface RuralInseminationReportProps extends RuralReportTemplateProps {
  reportType: InseminationReportType
  data?: InseminationReportEntry[] | InseminationStatisticsData | InseminationDeliveryData
}

export const inseminationReportTitleMap: Record<InseminationReportType, string> = {
  allInseminations: 'Inseminações realizadas',
  statistics: 'Estatísticas de repetição de inseminações',
  deliveriesByPeriod: 'Entregas por período',
  deliveriesByInseminator: 'Entregas por inseminador',
  deliveriesBySemenType: 'Entregas por tipo de sêmen',
  deliveriesByBull: 'Entregas por touro',
}

export const inseminationStatisticsGroupingLabelMap: Record<
  InseminationStatisticsGrouping,
  string
> = {
  inseminator: 'Inseminador',
  residence: 'Residência',
  semenType: 'Tipo de sêmen',
}

export const inseminationDeliveryGroupingLabelMap: Record<
  Exclude<InseminationDeliveryGrouping, 'period'>,
  string
> = {
  inseminator: 'Inseminador',
  semenType: 'Tipo de sêmen',
  bull: 'Touro',
}

export const inseminationReportColor = '#00A859'

const semenTypeLabelMap = semenType.keysAndLabels

export function formatInseminationReportDate(value?: Date | string | null) {
  if (!value) return 'Não informado'

  if (typeof value === 'string' && value.includes('T')) {
    const datePart = value.split('T')[0]
    const [year, month, day] = datePart.split('-')
    return `${day}/${month}/${year}`
  }

  return formatDate(new Date(value))
}

export function formatInseminationBoolean(value?: boolean | null) {
  return value ? 'Sim' : 'Não'
}

export function formatInseminationText(value?: string | null) {
  return value?.trim() || 'Não informado'
}

export function formatInseminationSemenType(value?: string | null) {
  if (!value) {
    return 'Não informado'
  }

  return semenTypeLabelMap?.[value as keyof typeof semenTypeLabelMap] ?? value
}

export function formatInseminationSuccessAverage(value?: number | null) {
  const normalizedValue = value ?? 0

  return `${normalizedValue.toFixed(2).replace('.', ',')}%`
}

export function formatInseminationQuantity(value?: number | null) {
  return formatAsBrNumber(value ?? 0, 0)
}

function buildResidenceLabel({
  residenceCode,
  residenceTitle,
  residenceFullAddress,
}: {
  residenceCode?: number | string | null
  residenceTitle?: string | null
  residenceFullAddress?: string | null
}) {
  const residenceParts = [
    residenceTitle?.trim() || null,
    residenceCode !== null && residenceCode !== undefined ? `Código ${residenceCode}` : null,
  ].filter(Boolean)

  if (residenceParts.length > 0) {
    return residenceParts.join(' - ')
  }

  if (residenceFullAddress?.trim()) {
    return residenceFullAddress.trim()
  }

  return null
}

export function buildInseminationResidenceName({
  ownerName,
  inseminationResidenceCode,
  inseminationResidenceTitle,
  inseminationResidenceFullAddress,
  citizenResidenceCode,
  citizenResidenceTitle,
  citizenResidenceFullAddress,
}: {
  ownerName?: string | null
  inseminationResidenceCode?: number | string | null
  inseminationResidenceTitle?: string | null
  inseminationResidenceFullAddress?: string | null
  citizenResidenceCode?: number | string | null
  citizenResidenceTitle?: string | null
  citizenResidenceFullAddress?: string | null
}) {
  const inseminationResidenceLabel = buildResidenceLabel({
    residenceCode: inseminationResidenceCode,
    residenceTitle: inseminationResidenceTitle,
    residenceFullAddress: inseminationResidenceFullAddress,
  })

  if (inseminationResidenceLabel) {
    return inseminationResidenceLabel
  }

  const citizenResidenceLabel = buildResidenceLabel({
    residenceCode: citizenResidenceCode,
    residenceTitle: citizenResidenceTitle,
    residenceFullAddress: citizenResidenceFullAddress,
  })

  if (citizenResidenceLabel) {
    return citizenResidenceLabel
  }

  if (ownerName?.trim()) {
    return `Sem residência vinculada - ${ownerName}`
  }

  return 'Não informado'
}
